/**
 * Confirm DOB Step (V2)
 *
 * Shown after OTP verification when the email matches an existing patient.
 * Patient must enter a DOB matching what's on file. After 3 failed attempts
 * the backend marks the session for fall-through to onboarding (the parent
 * hook handles routing).
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parse, isValid as isValidDate } from 'date-fns';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const schema = z.object({
  dob: z
    .string()
    .min(1, 'Date of birth is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Please enter a valid date'),
});
type DobFormData = z.infer<typeof schema>;

interface ConfirmDobStepProps {
  patientPreview: { first_name: string; middle_name?: string | null; last_name: string };
  onSubmit: (dob: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  attemptsRemaining: number | null;
}

export const ConfirmDobStep: React.FC<ConfirmDobStepProps> = ({
  patientPreview,
  onSubmit,
  loading,
  error,
  attemptsRemaining,
}) => {
  const form = useForm<DobFormData>({
    resolver: zodResolver(schema),
    defaultValues: { dob: '' },
    mode: 'onChange',
  });

  const handleFormSubmit = async (data: DobFormData) => {
    await onSubmit(data.dob);
  };

  return (
    <div>
      <h2 className='mb-2 text-2xl font-bold text-slate-900'>Confirm it&apos;s you</h2>
      <p className='mb-6 text-slate-500'>
        We found a record matching this email. To protect your privacy, please confirm your
        date of birth.
      </p>

      <div className='mb-5 flex items-center gap-3 rounded-lg border border-teal-200 bg-teal-50 p-3'>
        <ShieldCheck className='h-5 w-5 flex-shrink-0 text-teal-600' />
        <p className='text-sm text-slate-700'>
          Booking for{' '}
          <span className='font-semibold'>
            {[patientPreview.first_name, patientPreview.middle_name, patientPreview.last_name].filter(Boolean).join(' ')}
          </span>
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className='space-y-4'>
          <FormField
            control={form.control}
            name='dob'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-sm font-medium text-slate-700'>
                  Date of birth <span className='text-red-500'>*</span>
                </FormLabel>
                <FormControl>
                  <DatePicker
                    value={
                      field.value
                        ? (() => {
                            const d = parse(field.value, 'yyyy-MM-dd', new Date());
                            return isValidDate(d) ? d : undefined;
                          })()
                        : undefined
                    }
                    onValueChange={date => {
                      if (date && isValidDate(date)) {
                        field.onChange(format(date, 'yyyy-MM-dd'));
                      } else {
                        field.onChange('');
                      }
                    }}
                    placeholder='MM/DD/YYYY'
                    maxDate={new Date()}
                    minDate={new Date('1900-01-01')}
                    className='rounded-lg'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error && (
            <div className='rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
              {error}
              {attemptsRemaining !== null && attemptsRemaining > 0 && (
                <span className='mt-1 block text-xs text-red-600'>
                  {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining.
                </span>
              )}
            </div>
          )}

          <Button
            type='submit'
            disabled={!form.formState.isValid || loading}
            className='h-11 w-full rounded-lg bg-teal-600 text-white hover:bg-teal-700'
          >
            {loading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Verifying...
              </>
            ) : (
              'Confirm and continue'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};
